import React, { useEffect, useState } from 'react';
import Drawer from '../common/Drawer';
import Button from '../common/Button';
import DropdownField from '../common/formElements/DropdownField';
import { NetcashBatchDTO } from '../../services/apiClient';
import { useNetcashBatchApi } from '../../hooks/useNetcashBatchApi';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export interface NetcashBatchTarget {
  // Existing draft to add to - or, when absent, a new draft for year/month is created first
  batchId?: string;
  year: number;
  month: number;
}

interface AddToNetcashBatchDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  // What is being added, e.g. "5 selected invoices" or "every outstanding debit order invoice in the September 2026 invoice batch"
  subject: string;
  defaultYear: number;
  defaultMonth: number;
  isSubmitting: boolean;
  onConfirm: (target: NetcashBatchTarget) => void;
}

const NEW_DRAFT = 'new';

const AddToNetcashBatchDrawer: React.FC<AddToNetcashBatchDrawerProps> = ({
  isOpen,
  onClose,
  subject,
  defaultYear,
  defaultMonth,
  isSubmitting,
  onConfirm,
}) => {
  const { getDrafts } = useNetcashBatchApi();
  const [drafts, setDrafts] = useState<NetcashBatchDTO[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selection, setSelection] = useState<string>(NEW_DRAFT);
  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);

  useEffect(() => {
    if (!isOpen) return;

    setLoaded(false);
    setYear(defaultYear);
    setMonth(defaultMonth);

    getDrafts()
      .then((response) => {
        const list = (response?.data ?? []).filter((d) => !d.isPushing);
        setDrafts(list);
        const sameMonth = list.find((d) => d.year === defaultYear && d.month === defaultMonth);
        setSelection(sameMonth?.id ?? list[0]?.id ?? NEW_DRAFT);
      })
      .catch((error) => {
        console.error('Error loading draft Netcash batches:', error);
        setDrafts([]);
        setSelection(NEW_DRAFT);
      })
      .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const thisYear = new Date().getFullYear();
  const yearOptions = [thisYear - 1, thisYear, thisYear + 1];
  // Same selectable-card look as the member join opt-in cards
  const optionClass = (selected: boolean) =>
    `flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
      selected ? 'border-secondary bg-primary/5' : 'border-gray-200 hover:border-primary/40 dark:border-gray-700'
    }`;
  const monthOptions = monthNames.map((name, i) => ({ value: String(i + 1), label: name }));
  const yearDropdownOptions = yearOptions.map((y) => ({ value: String(y), label: String(y) }));

  const handleConfirm = () => {
    if (selection === NEW_DRAFT) {
      onConfirm({ year, month });
      return;
    }

    const draft = drafts.find((d) => d.id === selection);
    if (draft) onConfirm({ batchId: draft.id, year: draft.year ?? year, month: draft.month ?? month });
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Add to Netcash Batch" size="small">
      <div className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Choose the draft batch to add {subject} to, or start a new one. Invoices already in
          another draft, or already sent to Netcash and not rejected, are skipped so nobody is collected twice.
        </p>

        {!loaded ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading draft batches…</p>
        ) : (
          <div className="space-y-4">
            {drafts.map((draft) => (
              <label key={draft.id} className={optionClass(selection === draft.id)}>
                <input
                  type="radio"
                  name="netcash-target"
                  className="mt-1 h-4 w-4 accent-primary flex-shrink-0"
                  checked={selection === draft.id}
                  onChange={() => setSelection(draft.id ?? NEW_DRAFT)}
                />
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold text-primary dark:text-gray-200">
                    {monthNames[(draft.month ?? 1) - 1]} {draft.year} draft
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {draft.totalCount} invoice(s) | R{(draft.totalAmount ?? 0).toFixed(2)}
                  </p>
                </div>
              </label>
            ))}

            <label className={optionClass(selection === NEW_DRAFT)}>
              <input
                type="radio"
                name="netcash-target"
                className="mt-1 h-4 w-4 accent-primary flex-shrink-0"
                checked={selection === NEW_DRAFT}
                onChange={() => setSelection(NEW_DRAFT)}
              />
              <div className="flex-1 text-left">
                <p className="text-sm font-semibold text-primary dark:text-gray-200">Start a new draft batch</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Choose the month this batch is for.</p>
                {selection === NEW_DRAFT && (
                  <div className="flex gap-2 mt-3" onClick={(e) => e.preventDefault()}>
                    <div className="flex-1">
                      <DropdownField
                        id="netcash-new-draft-month"
                        value={String(month)}
                        options={monthOptions}
                        searchable={false}
                        onChange={(e) => setMonth(Number(e.target.value))}
                      />
                    </div>
                    <div className="w-28">
                      <DropdownField
                        id="netcash-new-draft-year"
                        value={String(year)}
                        options={yearDropdownOptions}
                        searchable={false}
                        onChange={(e) => setYear(Number(e.target.value))}
                      />
                    </div>
                  </div>
                )}
              </div>
            </label>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={handleConfirm} isLoading={isSubmitting} disabled={isSubmitting || !loaded}>
            Add to Batch
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default AddToNetcashBatchDrawer;
